import { t } from "i18next"
import { PageHeaderSection } from "../../components/common/PageHeadSection"
import { Alert, Col, Form, Input, Row, Spin } from "antd"
import { useEffect, useState } from "react"
import TextArea from "antd/es/input/TextArea"
import Title from "antd/es/typography/Title"
import { useMutation, useQuery } from "react-query"
import { PaymentService } from "../../services/PaymentService"
import dayjs from "dayjs"
import { PayableHotelSelect } from "../../components/payment/PayableHotelSelect"
import { mdlPaymentOrderCreateRequest } from "../../models/service-models/paymentOrder/PaymentOrderCreateRequest"
import { PaymentOrderService } from "../../services/PaymentOrderService"
import { toast } from "react-toastify"
import { ObjectHelper } from "../../utils/ObjectHelper"
import { useAppSelector } from "../../store/Hooks"
import { mdlPaymentOrderBankInfoRequest } from "../../models/service-models/paymentOrder/PaymentOrderBankInfoRequest"
import $ from 'jquery';
import { useSearchParams } from "react-router-dom"
import CookieManager from "../../utils/CookieManager"

export const Payment = () => {
    const siteStore = useAppSelector((store) => store.site);

    const formColSpan = 12;
    const formGutter = 16;

    const [selectedPayableHotel, setSelectedPayableHotel] = useState<string>();
    const [nextPaymentPeriod, setNextPaymentPeriod] = useState<string>("-");
    const [form] = Form.useForm();
    const [queryParameters, setQueryParameters] = useSearchParams();
    const PAYMENT_SUCCEEDED_QUERY_PARAMETER_NAME = "paymentSucceeded";
    const PAYMENT_PROCESS_API_URL = process.env.REACT_APP_API_ADDRESS + "/paymentOrder/processPaymentOrder";

    const handlePayableHotelSelection = (hotelId: string) => {
        setSelectedPayableHotel(hotelId);
    }

    const {
        data: hotelPaymentData,
        isLoading: isHotelPaymentDataLoading,
        refetch: refetchHotelPaymentData
    } = useQuery(
        ["getHotelPaymentData", selectedPayableHotel],
        () => {
            if (selectedPayableHotel) {
                return PaymentService.GetHotelPaymentData({ hotelId: selectedPayableHotel });
            }
        },
        {
            refetchOnWindowFocus: false,
            enabled: !!selectedPayableHotel,
            cacheTime: 0
        }
    );

    const getNextPaymentDate = () => {
        return dayjs().add(1, 'year').format("DD.MM.YYYY");
    }

    let paymentData = hotelPaymentData?.body?.data;

    useEffect(() => {
        if (paymentData) {
            form.resetFields();

            form.setFieldsValue({
                businessTitle: paymentData.hotel?.companyTitle,
                taxOffice: paymentData.hotel?.taxAdministration,
                taxNumber: paymentData.hotel?.taxNumber,
                businessMailAddress: paymentData.hotel?.accountancyEmail,
                accountingAddress: paymentData.hotel?.taxAddress
            });
            setNextPaymentPeriod(getNextPaymentDate());
        }
    }, [paymentData]);

    const {
        mutateAsync: createPaymentOrder,
        isLoading: createPaymentOrderLoading,
        data: mutateCreatePaymentOrder
    } = useMutation(
        async (data: mdlPaymentOrderCreateRequest) => {
            return PaymentOrderService.CreatePaymentOrder(data);
        },
        {
            onMutate(variables) {
                variables.hotelId = selectedPayableHotel;
            },
            onError: error => {
                console.log(error);
            },
            async onSuccess(data, variables, context) {
                if (data && data.header.success && data?.body?.data?.orderId) {
                    await getPaymentOrderBankInfo({ orderId: data?.body?.data?.orderId });
                }
                else {
                    toast.error(ObjectHelper.getErrorMessage(data.header.messages));
                }
            }
        }
    )

    const {
        mutateAsync: getPaymentOrderBankInfo,
        isLoading: isPaymentOrderBankInfoLoading,
        data: paymentOrderBankInfoData
    } = useMutation(
        async (data: mdlPaymentOrderBankInfoRequest) => (PaymentOrderService.GetPaymentOrderBankInfo(data)),
        {
            onMutate(variables) {
                variables.accountancyEmail = form.getFieldValue("businessMailAddress");
                variables.companyTitle = form.getFieldValue("businessTitle");
                variables.hotelId = selectedPayableHotel;
                variables.taxAddress = form.getFieldValue("accountingAddress");
                variables.taxAdministration = form.getFieldValue("taxOffice");
                variables.taxNumber = form.getFieldValue("taxNumber");

                const cardHolder: string = form.getFieldValue("nameSurname");
                const cardHolderArray: string[] = cardHolder?.split(" ") || [];
                let maskedCardHolder: string = "";
                cardHolderArray.forEach((item: string) => {
                    if (maskedCardHolder) {
                        maskedCardHolder += " ";
                    }
                    maskedCardHolder += item.charAt(0) + "***";
                });
                variables.cardHolder = maskedCardHolder;

                const cardNo: string = form.getFieldValue("cardNo");
                variables.cardNo = cardNo?.substring(cardNo?.length - 4);
            },
            onError: error => {
                console.log(error);
            },
            onSuccess(data, variables, context) {
                if (data && data.header.success && data?.body?.data?.isSuccessful === true) {
                    toast.success(t("common.success"));
                }
                else {
                    toast.error(ObjectHelper.getErrorMessage(data.header.messages));
                }
            }
        }
    )

    let bankFormData = paymentOrderBankInfoData?.body?.data;

    useEffect(() => {
        if (bankFormData && bankFormData.isSuccessful === true) {
            $("#bankFormSubmitButton").trigger("click");
        }
    }, [bankFormData]);

    return (
        <>
            <div className="nk-content ">
                <div className="container-fluid">
                    <div className="nk-content-inner">
                        <div className="nk-content-body">
                            <PageHeaderSection
                                pageTitle={t("payment.title")}
                            />
                            {queryParameters?.get(PAYMENT_SUCCEEDED_QUERY_PARAMETER_NAME) === "true" && <Alert className="mb-2 fw-bold" showIcon type="success" message={t("payment.paymentSucceeded")} />}
                            {queryParameters?.get(PAYMENT_SUCCEEDED_QUERY_PARAMETER_NAME) === "false" && <Alert className="mb-2 fw-bold" showIcon type="error" message={t("payment.paymentFailed") + ' (' + queryParameters?.get("message") + ')'} />}
                            <Alert className="mb-2" type="warning" showIcon message={
                                <>
                                    <label>{t("payment.requiredFields")}</label><br />
                                    <label>{t("payment.paymentBusinessInformationWarning")}</label><br />
                                    <label>{t("payment.paymentBusinessCreditCardWarning")}</label><br />
                                    <label className="fw-bold">{t("payment.paymentAmountWarning")}</label>
                                </>
                            } />
                            <div className="nk-block">
                                <div className="row g-gs">
                                    <div className="col-12">
                                        <div className="card card-bordered h-100">
                                            <div className="card-inner">
                                                <Spin
                                                    spinning={isHotelPaymentDataLoading || createPaymentOrderLoading || isPaymentOrderBankInfoLoading}
                                                >
                                                    <Form
                                                        form={form}
                                                        name="payment"
                                                        autoComplete="off"
                                                        layout="vertical"
                                                        className="mt-2"
                                                        onFinish={async (values) => await createPaymentOrder({})}
                                                    >
                                                        <Row gutter={formGutter}>
                                                            <Col span={formColSpan}>
                                                                <Title level={4}>{t("payment.businessInformation")}</Title>
                                                                <Form.Item
                                                                    label={t("payment.hotel")}
                                                                >
                                                                    <PayableHotelSelect onChange={handlePayableHotelSelection} />
                                                                </Form.Item>
                                                                <Form.Item
                                                                    name="businessTitle"
                                                                    label={t("payment.businessTitle")}
                                                                    rules={[{ required: true, message: t("common.required") }]}
                                                                >
                                                                    <Input placeholder={t("payment.businessTitle")} />
                                                                </Form.Item>
                                                                <Row gutter={formGutter}>
                                                                    <Col span={formColSpan}>
                                                                        <Form.Item
                                                                            name="taxOffice"
                                                                            label={t("payment.taxOffice")}
                                                                            rules={[{ required: true, message: t("common.required") }]}
                                                                        >
                                                                            <Input placeholder={t("payment.taxOffice")} />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    <Col span={formColSpan}>
                                                                        <Form.Item
                                                                            name="taxNumber"
                                                                            label={t("payment.taxNumber")}
                                                                            rules={[{ required: true, message: t("common.required") }]}
                                                                        >
                                                                            <Input placeholder={t("payment.taxNumber")} />
                                                                        </Form.Item>
                                                                    </Col>
                                                                </Row>
                                                                <Form.Item
                                                                    name="businessMailAddress"
                                                                    label={t("payment.businessMailAddress")}
                                                                    rules={[
                                                                        { required: true, message: t("common.required") },
                                                                        { type: "email", message: t("common.invalidEmail") }
                                                                    ]}
                                                                >
                                                                    <Input placeholder={t("payment.businessMailAddress")} />
                                                                </Form.Item>
                                                                <Form.Item
                                                                    name="accountingAddress"
                                                                    label={t("payment.accountingAddress")}
                                                                    rules={[{ required: true, message: t("common.required") }]}
                                                                >
                                                                    <TextArea placeholder={t("payment.accountingAddress")} rows={4} />
                                                                </Form.Item>

                                                                <Title level={4}>{t("payment.creditCardInformation")}</Title>
                                                                <Form.Item
                                                                    name="nameSurname"
                                                                    label={t("payment.nameSurname")}
                                                                    rules={[
                                                                        { required: true, message: t("common.required") },
                                                                        { pattern: /\s/, message: t("common.required") }
                                                                    ]}
                                                                >
                                                                    <Input placeholder={t("payment.nameSurname")} />
                                                                </Form.Item>
                                                                <Form.Item
                                                                    name="cardNo"
                                                                    label={t("payment.cardNo")}
                                                                    rules={[
                                                                        { required: true, message: t("common.required") },
                                                                        { min: 4, message: t("common.required") }
                                                                    ]}
                                                                >
                                                                    <Input placeholder={t("payment.cardNo")} />
                                                                </Form.Item>
                                                                <Row gutter={formGutter}>
                                                                    <Col span={formColSpan}>
                                                                        <Form.Item
                                                                            name="validity"
                                                                            label={t("payment.validity")}
                                                                            rules={[
                                                                                { required: true, message: t("common.required") },
                                                                                { min: 4, message: t("common.required") },
                                                                                { max: 4, message: t("common.required") }
                                                                            ]}
                                                                        >
                                                                            <Input placeholder={t("payment.validityPattern")} maxLength={4} />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    <Col span={formColSpan}>
                                                                        <Form.Item
                                                                            name="cvv"
                                                                            label={t("payment.cvv")}
                                                                            rules={[
                                                                                { required: true, message: t("common.required") },
                                                                                { min: 3, message: t("common.required") },
                                                                                { max: 4, message: t("common.required") },
                                                                            ]}
                                                                        >
                                                                            <Input placeholder={t("payment.cvv")} maxLength={4} />
                                                                        </Form.Item>
                                                                    </Col>
                                                                </Row>
                                                                <button disabled={!selectedPayableHotel} type="submit" className="btn btn-primary w-100" style={{ textAlign: "center", justifyContent: "center" }}>{t("payment.makePayment")}</button>
                                                            </Col>
                                                            <Col span={formColSpan}>
                                                                <Title level={4}>{t("payment.paymentDetails")}</Title>
                                                                <table className="table table-hover">
                                                                    <thead>
                                                                        <tr>
                                                                            <th scope="col">{t("payment.hotelName")}</th>
                                                                            <th scope="col">{t("payment.quantity")}</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        <tr>
                                                                            <th scope="row">{paymentData?.hotel?.localName || "-"}</th>
                                                                            <td>x1</td>
                                                                        </tr>
                                                                        <tr style={{ background: "#bf6600" }}>
                                                                            <th scope="row">{t("payment.totalAnnualAmount")?.toUpperCase()}</th>
                                                                            <td><b>{paymentData?.payment?.amount} {paymentData?.payment?.currencyCode} ({paymentData?.amountTry} TL)</b></td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                                <Title level={4} className="mt-2">{t("payment.nextPaymentPeriod")} : {nextPaymentPeriod}</Title>
                                                                <Title level={4}>{t("payment.support")}</Title>
                                                                <label>{t("payment.supportText")}</label>
                                                                <br />
                                                                <a href={"mailto:" + t("payment.supportMail")}>{t("payment.supportMail")}</a>
                                                            </Col>
                                                        </Row>
                                                    </Form>
                                                    {bankFormData && bankFormData?.isSuccessful === true &&
                                                        <form
                                                            id="bankForm"
                                                            name="bankForm"
                                                            autoCapitalize="false"
                                                            autoComplete="false"
                                                            autoCorrect="false"
                                                            autoFocus={false}
                                                            autoSave="false"
                                                            action={bankFormData?.bankInfo?.bankUrl}
                                                            method="post"
                                                            hidden
                                                        >
                                                            <input hidden name="H2SUserId" value={siteStore.user?.userId}></input>
                                                            <input hidden name="H2SUserName" value={siteStore.user?.userName}></input>
                                                            <input hidden name="Pan" value={form.getFieldValue("cardNo")}></input>
                                                            <input hidden name="Cvv2" value={form.getFieldValue("cvv")}></input>
                                                            <input hidden name="Expiry" value={form.getFieldValue("validity")}></input>
                                                            <input hidden name="CardHolderName" value={form.getFieldValue("nameSurname")}></input>
                                                            <input hidden name="MbrId" value={bankFormData?.bankInfo?.mbrId}></input>
                                                            <input hidden name="MerchantID" value={bankFormData?.bankInfo?.merchantId}></input>
                                                            <input hidden name="UserCode" value={bankFormData?.bankInfo?.userCode}></input>
                                                            <input hidden name="UserPass" value={bankFormData?.bankInfo?.userPass}></input>
                                                            <input hidden name="SecureType" value={bankFormData?.bankInfo?.secureType}></input>
                                                            <input hidden name="TxnType" value={bankFormData?.bankInfo?.txnType}></input>
                                                            <input hidden name="InstallmentCount" value={bankFormData?.bankInfo?.installmentCount}></input>
                                                            <input hidden name="Currency" value={bankFormData?.bankInfo?.currency}></input>
                                                            <input hidden name="OkUrl" value={bankFormData?.bankInfo?.okUrl}></input>
                                                            <input hidden name="FailUrl" value={bankFormData?.bankInfo?.failUrl}></input>
                                                            <input hidden name="OrderId" value={bankFormData?.orderId}></input>
                                                            <input hidden name="OrgOrderId" value=""></input>
                                                            <input hidden name="PurchAmount" value={bankFormData?.purchAmount}></input>
                                                            <input hidden name="Lang" value="TR"></input>
                                                            <input hidden name="Rnd" value={bankFormData?.rnd}></input>
                                                            <input hidden name="Hash" value={bankFormData?.hash}></input>
                                                            <input hidden name="HotelId" value={selectedPayableHotel}></input>
                                                            <input hidden name="CurrentLanguage" value={siteStore.language || "en-US"}></input>
                                                            <input hidden name="RedirectionUrl" value={window.location.href}></input>
                                                            <input hidden name="AuthToken" value={"Bearer " + CookieManager.get("authToken")}></input>
                                                            <button hidden id="bankFormSubmitButton" type="submit"></button>
                                                        </form>
                                                    }
                                                </Spin>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}